import { useMsal } from '@azure/msal-react';
import { notification } from 'antd';
import { useState } from 'react';
import {
	LimitationDto,
	LimitationType,
	LimitationValue,
} from '../../api-client';
import { AdminApiClient } from '../../providers/api-provider';

export const useLimitations = () => {
	const { instance } = useMsal();

	const [limitationValues, setLimitationValues] = useState<LimitationValue[]>();
	const [limitationValuesLoading, setLimitationValuesLoading] = useState(false);

	const handleLimitationValuesByType = async (type: LimitationType) => {
		setLimitationValuesLoading(true);
		try {
			const adminApi = await AdminApiClient(instance);
			const response = await adminApi.getAllLimitationValuesByType(type);
			setLimitationValues(response.data ?? []);
		} catch {
			setLimitationValues([]);
			notification.error({
				message: 'Unable to retrieve limitation values!',
				description: 'Unable to retrieve limitation values.',
				duration: 2,
			});
		} finally {
			setLimitationValuesLoading(false);
		}
	};

	const [limitations, setLimitations] = useState<LimitationDto[]>();
	const [limitationsLoading, setLimitationsLoading] = useState(false);

	const handleLimitationsBySapCode = async (sapCode?: string) => {
		setLimitationsLoading(true);
		try {
			const adminApi = await AdminApiClient(instance);
			const response = await adminApi.getAllLimitationsBySapCode(sapCode);
			setLimitations(response.data ?? []);
		} catch {
			setLimitations([]);
			notification.error({
				message: 'Unable to retrieve limitations!',
				description: 'Unable to retrieve limitations.',
				duration: 2,
			});
		} finally {
			setLimitationsLoading(false);
		}
	};

	// Import limitations (file)
	const [importLimitationsLoading, setImportLimitationsLoading] =
		useState(false);
	const [importLimitationsSuccess, setImportLimitationsSuccess] =
		useState(false);

	const resetImportLimitationsState = () => setImportLimitationsSuccess(false);

	const importLimitations = async (file: File) => {
		if (!file) {
			setImportLimitationsSuccess(false);
			return;
		}

		try {
			setImportLimitationsLoading(true);
			setImportLimitationsSuccess(false);

			const adminApi = await AdminApiClient(instance);
			await adminApi.importLimitations(file);

			setImportLimitationsSuccess(true);
		} catch {
			setImportLimitationsSuccess(false);
			notification.error({
				message: 'Import failed',
				description: 'Unable to import limitations.',
				duration: 2,
			});
		} finally {
			setImportLimitationsLoading(false);
		}
	};

	// Remove limitation
	const [removeLimitationLoading, setRemoveLimitationLoading] = useState(false);
	const [removeLimitationSuccess, setRemoveLimitationSuccess] = useState(false);

	const resetRemoveLimitationState = () => setRemoveLimitationSuccess(false);

	const removeLimitation = async (limitation: LimitationDto) => {
		if (!limitation?.sapCode || !limitation?.value || !limitation?.type) {
			setRemoveLimitationSuccess(false);
			return;
		}

		try {
			setRemoveLimitationLoading(true);
			setRemoveLimitationSuccess(false);

			const adminApi = await AdminApiClient(instance);
			await adminApi.removeLimitation(limitation);

			setRemoveLimitationSuccess(true);
		} catch {
			setRemoveLimitationSuccess(false);
			notification.error({
				message: 'Operation failed',
				description: 'Unable to remove limitation.',
				duration: 2,
			});
		} finally {
			setRemoveLimitationLoading(false);
		}
	};

	// =========================
	// Export limitations (xlsx)
	// =========================
	const [exportLimitationsLoading, setExportLimitationsLoading] =
		useState(false);

	const exportLimitations = async () => {
		try {
			setExportLimitationsLoading(true);

			const adminApi = await AdminApiClient(instance);
			const res = await adminApi.exportLimitations();

			const disposition =
				(res as any)?.headers?.['content-disposition'] ??
				(res as any)?.headers?.['Content-Disposition'];

			let filename = `limitations_export_${new Date()
				.toISOString()
				.slice(0, 10)}.xlsx`;

			if (typeof disposition === 'string') {
				const match =
					disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i) ??
					disposition.match(/filename="?([^"]+)"?/i);

				if (match?.[1]) {
					filename = decodeURIComponent(
						match[1].trim().replace(/^"|"$|'/g, ''),
					);
				}
			}

			const contentType =
				(res as any)?.headers?.['content-type'] ??
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

			const blob = new Blob([res.data as any], { type: contentType });
			const url = window.URL.createObjectURL(blob);

			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			notification.error({
				message: 'Export failed',
				description: 'Unable to export limitations.',
				duration: 2,
			});
		} finally {
			setExportLimitationsLoading(false);
		}
	};

	// ==========================================
	// Export limitations import template (xlsx)
	// ==========================================
	const [
		exportLimitationsTemplateLoading,
		setExportLimitationsTemplateLoading,
	] = useState(false);

	const exportLimitationsImportTemplate = async () => {
		try {
			setExportLimitationsTemplateLoading(true);

			const adminApi = await AdminApiClient(instance);
			const res = await adminApi.exportLimitationsImportTemplate();

			const disposition =
				(res as any)?.headers?.['content-disposition'] ??
				(res as any)?.headers?.['Content-Disposition'];

			let filename = `limitations_import_template_${new Date()
				.toISOString()
				.slice(0, 10)}.xlsx`;

			if (typeof disposition === 'string') {
				const match =
					disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i) ??
					disposition.match(/filename="?([^"]+)"?/i);

				if (match?.[1]) {
					filename = decodeURIComponent(
						match[1].trim().replace(/^"|"$|'/g, ''),
					);
				}
			}

			const contentType =
				(res as any)?.headers?.['content-type'] ??
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

			const blob = new Blob([res.data as any], { type: contentType });
			const url = window.URL.createObjectURL(blob);

			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			notification.error({
				message: 'Export failed',
				description: 'Unable to export limitations template.',
				duration: 2,
			});
		} finally {
			setExportLimitationsTemplateLoading(false);
		}
	};

	return {
		limitationValues,
		limitationValuesLoading,
		handleLimitationValuesByType,

		limitations,
		limitationsLoading,
		handleLimitationsBySapCode,

		importLimitations,
		importLimitationsLoading,
		importLimitationsSuccess,
		resetImportLimitationsState,

		removeLimitation,
		removeLimitationLoading,
		removeLimitationSuccess,
		resetRemoveLimitationState,

		exportLimitations,
		exportLimitationsLoading,

		exportLimitationsImportTemplate,
		exportLimitationsTemplateLoading,
	};
};
